import { useEffect, useMemo } from 'react'
import {
  EntityCommandDTO,
  FormRow,
  ObjectDataRecord,
  ObjectWithRecordsType,
  RequestParams,
} from 'src/types'

import { useFetchObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchObjectDataWithFixedKey'

import {
  getCommandRequestData,
  getReadObjectCommandConfig,
  prepareRequestParamsWithExpands,
} from '@helpers'
import { FIELD_VALUE_TYPE } from '@constants'

import { getExpandsParamForForm } from '../helpers'
import { mapCoreKeysToFormKeys } from '../helpers/mapCoreKeysToFormKeys'
import { ExecuteCommandGetArgs } from '../types'

type ObjectModelCommand = { code?: string; name?: string; flags?: string[]; httpMethod: string }

type UseFetchRowByUpdateProps = {
  objectsWithDirtyRecords: ObjectWithRecordsType
  objectCode: string
  objectId?: string
  globalId: string
  typeId?: FIELD_VALUE_TYPE
  fields: FormRow[]
  overrideCommands?: EntityCommandDTO[]
  objectCommands?: ObjectModelCommand[]
  handleResetFormWithNewRowData: (data: { row: ObjectDataRecord }) => void
}

export const useFetchRowByUpdate = ({
  objectsWithDirtyRecords,
  objectCode,
  objectId,
  globalId,
  typeId,
  fields,
  overrideCommands,
  objectCommands,
  handleResetFormWithNewRowData,
}: UseFetchRowByUpdateProps) => {
  const [fetchRow] = useFetchObjectDataWithFixedKey(globalId, typeId, fields)

  const object = objectsWithDirtyRecords[objectCode]

  const fetchConfig = useMemo<ExecuteCommandGetArgs | null>(() => {
    if (!objectId || !object || !Object.keys(object).length) {
      return null
    }

    const readCommandConfig = getReadObjectCommandConfig({
      objectId,
      typeId,
      overrideCommands,
      objectCommands,
    })

    if (!readCommandConfig) {
      return null
    }

    const { command: readCommand, sourceParams, defaultParams } = readCommandConfig

    const computedParams: RequestParams = readCommand.parameters?.length
      ? getCommandRequestData(readCommand.parameters, sourceParams)
      : readCommand.isOverridden
      ? {}
      : defaultParams

    const computedBody = readCommand.body?.length
      ? getCommandRequestData(readCommand.body, sourceParams)
      : undefined

    const formExpands = getExpandsParamForForm(fields)
    const { requestParams, expands } = prepareRequestParamsWithExpands(computedParams, formExpands)

    return {
      method: readCommand.httpMethod,
      objectCode: readCommand.objectCode || objectCode,
      commandCode: readCommand.commandCode,
      resource: readCommand.resource,
      params: requestParams,
      body: computedBody,
      expands,
    }
  }, [objectId, object, overrideCommands, objectCommands, fields, objectCode, typeId])

  useEffect(() => {
    if (!fetchConfig) {
      return
    }

    fetchRow(fetchConfig)
      .unwrap()
      .then(newRowData => {
        const rowData = Array.isArray(newRowData) ? newRowData[0] : newRowData
        const mappedRowData = mapCoreKeysToFormKeys(rowData, fields)

        handleResetFormWithNewRowData({ row: mappedRowData })
      })
  }, [fetchConfig, fetchRow, fields, handleResetFormWithNewRowData])
}
